import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Columns2 } from 'lucide-react';
import { Button } from '../ui/Button';

export type ProductCardColumn = 'categoria' | 'descrizione' | 'costo' | 'prezzo' | 'unita' | 'stato';

export const ALL_COLUMNS: ProductCardColumn[] = ['categoria', 'descrizione', 'costo', 'prezzo', 'unita', 'stato'];

export const DEFAULT_VISIBLE: Record<ProductCardColumn, boolean> = {
  categoria: true,
  descrizione: true,
  costo: false,
  prezzo: true,
  unita: true,
  stato: true,
};

const LABELS: Record<ProductCardColumn, string> = {
  categoria: 'Categoria',
  descrizione: 'Descrizione',
  costo: 'Costo',
  prezzo: 'Prezzo',
  unita: 'Unità',
  stato: 'Stato',
};

interface Props {
  visible: Record<ProductCardColumn, boolean>;
  onChange: (key: ProductCardColumn, visible: boolean) => void;
}

export function ProductColumnToggle({ visible, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const justOpenedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (justOpenedRef.current) { justOpenedRef.current = false; return; }
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler, { capture: true });
    return () => document.removeEventListener('pointerdown', handler, { capture: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const rect = triggerRef.current?.getBoundingClientRect();

  const menu = open && rect
    ? createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right,
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 9999,
            minWidth: 180,
            padding: '0.375rem',
          }}
        >
          {ALL_COLUMNS.map(key => (
            <label
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.625rem',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text)',
                cursor: 'pointer',
                borderRadius: 'var(--radius-interactive)',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <input
                type="checkbox"
                checked={visible[key]}
                onChange={e => onChange(key, e.target.checked)}
                style={{ accentColor: 'var(--color-primary)' }}
              />
              {LABELS[key]}
            </label>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={triggerRef} style={{ display: 'inline-flex' }}>
      <Button
        size="sm"
        variant="secondary"
        icon={<Columns2 size={14} />}
        onClick={() => { justOpenedRef.current = true; setOpen(o => !o); }}
      >
        Colonne
      </Button>
      {menu}
    </div>
  );
}
